import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { cx } from './cx';

export interface SummaryAction {
  to: string;
  text: string;
  visuallyHiddenText?: string;
  state?: unknown;
}

export interface SummaryRow {
  key: ReactNode;
  value: ReactNode;
  keyLang?: string;
  actions?: SummaryAction[];
}

/**
 * govuk/components/summary-list. Rows without actions in a list that has actions get the
 * `govuk-summary-list__row--no-actions` modifier instead of an empty <span> inside the <dl> (invalid HTML).
 */
export function SummaryList({ rows, noBorder, className }: { rows: SummaryRow[]; noBorder?: boolean; className?: string }) {
  const anyActions = rows.some((r) => r.actions && r.actions.length > 0);
  return (
    <dl className={cx('govuk-summary-list', noBorder && 'govuk-summary-list--no-border', className)}>
      {rows.map((row, i) => (
        <div key={i} className={cx('govuk-summary-list__row', anyActions && !row.actions?.length && 'govuk-summary-list__row--no-actions')}>
          <dt className="govuk-summary-list__key" lang={row.keyLang}>
            {row.key}
          </dt>
          <dd className="govuk-summary-list__value">{row.value}</dd>
          {row.actions && row.actions.length > 0 && (
            <dd className="govuk-summary-list__actions">
              {row.actions.length === 1 ? (
                <ActionLink action={row.actions[0]!} />
              ) : (
                <ul className="govuk-summary-list__actions-list">
                  {row.actions.map((a, j) => (
                    <li className="govuk-summary-list__actions-list-item" key={j}>
                      <ActionLink action={a} />
                    </li>
                  ))}
                </ul>
              )}
            </dd>
          )}
        </div>
      ))}
    </dl>
  );
}

function ActionLink({ action }: { action: SummaryAction }) {
  return (
    <Link className="govuk-link" to={action.to} state={action.state}>
      {action.text}
      {action.visuallyHiddenText && (
        <>
          {' '}
          <span className="govuk-visually-hidden">{action.visuallyHiddenText}</span>
        </>
      )}
    </Link>
  );
}
